import { DataService } from '../../dataService';
import { AxiosPromise } from 'axios';
import { Opportunity } from '../models/OpportunityModels';
import { TransactionTeam } from '../models/TransactionTeamModels';
import {
  AimAttachment,
  AimIdAttachmentPostParams,
  OpportunityAttachmentPostParams,
  TransactionTeamAttachmentPostParams
} from '../models/AttachmentModels';
import { AxiosProgressEvent } from 'axios';
import { ENV } from '../envUtils';

/**
 * Factory function that constructs the attachment controller with CRUD methods for managing attachments on AIM entities via the DataService HTTP client.
 *
 * Takes a DataService instance and returns methods for adding, fetching, updating, and deleting attachments on opportunities, transaction teams, and other AIM resources.
 */
export const attachmentsController = (api: DataService) => ({
  getOpportunityAttachments: ({
    aimOpportunity
  }: Pick<Opportunity, 'aimOpportunity'>): AxiosPromise<AimAttachment[]> =>
    api.get({ route: [`${ENV}/opportunity/${aimOpportunity}/attachments`] }),
  getTransactionTeamAttachments: ({
    aimTransactionTeam
  }: Pick<TransactionTeam, 'aimTransactionTeam'>): AxiosPromise<AimAttachment[]> =>
    api.get({ route: [`${ENV}/transactionteam/${aimTransactionTeam}/attachments`] }),
  getAttachmentsByAimId: ({ aimId }: Pick<AimAttachment, 'aimId'>): AxiosPromise<AimAttachment[]> =>
    api.get({ route: [`${ENV}/${aimId}/attachments`] }),
  getAttachmentByPid: ({ pid }: { pid: string }): AxiosPromise<AimAttachment[]> =>
    api.get({ route: [`${ENV}/attachment/${pid}`] }),
  addAttachmentsByAimId: (
    payload: AimIdAttachmentPostParams,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/${payload.aimId}/attachments`],
      data: payload.attachments,
      onUploadProgress
    }),
  addOpportunityAttachments: (
    payload: OpportunityAttachmentPostParams,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/opportunity/${payload.aimOpportunity}/attachments`],
      data: payload.attachments,
      onUploadProgress
    }),
  addTransactionTeamAttachments: (
    payload: TransactionTeamAttachmentPostParams,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/transactionteam/${payload.aimTransactionTeam}/attachments`],
      data: payload.attachments,
      onUploadProgress
    }),
  deleteAttachment: ({ pid }: Pick<AimAttachment, 'pid'>): AxiosPromise<void> =>
    api.delete({
      route: [`${ENV}/attachment/${pid}`],
      data: {}
    }),
  bulkDeleteAttachments: ({ aimId }: { aimId: string }): AxiosPromise<void> =>
    api.delete({
      route: [`${ENV}/${aimId}/attachments`],
      data: {}
    }),
  getFileBlob: ({
    pid,
    onDownloadProgress
  }: {
    pid: string;
    onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
  }): AxiosPromise<Blob> => api.getBlob({ route: [`${ENV}/attachment/${pid}/download`], onDownloadProgress })
});
